import { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { toast } from 'sonner';

interface UserProfile {
  id: string;
  username: string;
  bio: string;
  tagline: string;
  longBio: string;
  focusTags: string[];
  externalLinks: Array<{ label: string; url: string }>;
  avatarId: number;
  isSubscriber: boolean;
  isDonator: boolean;
  subscriptionTier: string | null;
  createdAt: string;
  hideComments?: boolean;
  hideLikes?: boolean;
  hidePosts?: boolean;
  is_demo_user?: boolean;
  badges?: Array<{
    id: string;
    name: string;
    description: string;
    earned_at: string;
  }>;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, username: string) => Promise<void>;
  signInWithSocialProvider: (provider: string) => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const initRef = useRef(false);

  const fetchUserProfile = async (userId: string) => {
    try {
      console.log('[AUTH] Fetching profile for user:', userId);
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('[AUTH] Error fetching user profile:', error);
        return;
      }

      if (profileData) {
        console.log('[AUTH] Profile fetched successfully');
        setUserProfile({
          id: profileData.id,
          username: profileData.username || '',
          bio: profileData.bio || '',
          tagline: profileData.tagline || '',
          longBio: profileData.long_bio || '',
          focusTags: Array.isArray(profileData.focus_tags) ? profileData.focus_tags : [],
          externalLinks: Array.isArray(profileData.external_links) 
            ? (profileData.external_links as Array<{ label: string; url: string }>)
            : [],
          avatarId: profileData.avatar_id || 1,
          isSubscriber: profileData.is_subscriber || false,
          isDonator: profileData.is_donator || false,
          subscriptionTier: profileData.subscription_tier || null,
          createdAt: profileData.created_at || '',
          hideComments: profileData.hide_comments || false,
          hideLikes: profileData.hide_likes || false,
          hidePosts: profileData.hide_posts || false,
          is_demo_user: profileData.is_demo_user || false,
          badges: Array.isArray(profileData.badges) ? profileData.badges as Array<{
            id: string;
            name: string;
            description: string;
            earned_at: string;
          }> : [],
        });
      } else {
        console.log('[AUTH] No profile found for user');
      }
    } catch (error) {
      console.error('[AUTH] Error fetching user profile:', error);
    }
  };

  useEffect(() => {
    let mounted = true;
    console.log('[AUTH] Setting up auth...');

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, currentSession) => {
      console.log('[AUTH] Auth state change:', { 
        event, 
        hasSession: !!currentSession,
        userId: currentSession?.user?.id 
      });
      
      if (!mounted) return;

      // Update state synchronously
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      
      // Handle profile fetching with setTimeout to prevent deadlock
      if (currentSession?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        setTimeout(() => {
          if (mounted) {
            fetchUserProfile(currentSession.user.id);
          }
        }, 0);
      } else if (event === 'SIGNED_OUT') {
        console.log('[AUTH] User signed out, clearing profile');
        setUserProfile(null);
      }
      
      // Set loading to false after first auth state change
      if (event === 'INITIAL_SESSION') {
        setLoading(false);
      }
    });

    // THEN get initial session
    const initializeAuth = async () => {
      try {
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[AUTH] Error getting initial session:', error);
          if (mounted) setLoading(false);
          return;
        }

        console.log('[AUTH] Initial session:', {
          hasSession: !!initialSession,
          userId: initialSession?.user?.id
        });

        if (mounted && initialSession?.user) {
          setSession(initialSession);
          setUser(initialSession.user);
          // Fetch profile for existing session
          await fetchUserProfile(initialSession.user.id);
        }
        
        if (mounted) setLoading(false);
      } catch (error) {
        console.error('[AUTH] Error initializing auth:', error);
        if (mounted) setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      console.log('[AUTH] Signing in:', email);
      
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        console.error('[AUTH] Sign in error:', error);
        
        let errorMessage = error.message || 'Failed to sign in';
        if (error.message?.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (error.message?.includes('Email not confirmed')) {
          errorMessage = 'Please check your email and click the confirmation link before signing in.';
        } else if (error.message?.includes('Too many requests')) {
          errorMessage = 'Too many login attempts. Please wait a moment before trying again.';
        }
        
        toast.error(errorMessage);
        throw error;
      }
      
      console.log('[AUTH] Sign in successful:', data.user?.id);
      toast.success('Welcome back!');
    } catch (error: any) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, username: string) => {
    try {
      setLoading(true);
      console.log('[AUTH] Signing up:', { email, username });
      
      const { data, error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          data: { username },
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) {
        console.error('[AUTH] Sign up error:', error);
        
        let errorMessage = error.message || 'Failed to sign up';
        if (error.message?.includes('Database error saving new user')) {
          errorMessage = 'There was an issue creating your profile. Please try again.';
        } else if (error.message?.includes('User already registered')) {
          errorMessage = 'An account with this email already exists. Please try signing in instead.';
        } else if (error.message?.includes('Password should be at least')) {
          errorMessage = 'Password must be at least 6 characters long.';
        }
        
        toast.error(errorMessage);
        throw error;
      }
      
      console.log('[AUTH] Sign up successful:', data.user?.id);
      
      // Check if email confirmation is required
      if (data.user && !data.session) {
        toast.success('Please check your email to confirm your account');
      } else if (data.session) {
        toast.success('Account created successfully!');
      }
    } catch (error: any) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signInWithSocialProvider = async (provider: string) => {
    try {
      console.log('[AUTH] Starting OAuth with:', provider);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider as any,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            prompt: 'select_account',
            access_type: 'offline'
          }
        }
      });
      
      if (error) {
        console.error('[AUTH] OAuth error:', error);
        toast.error(error.message || 'Failed to sign in with ' + provider);
        throw error;
      }
      
      console.log('[AUTH] OAuth redirect initiated');
      // User will be redirected to provider
    } catch (error: any) {
      throw error;
    }
  };

  const signOut = async () => {
    try {
      console.log('[AUTH] Signing out');
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('[AUTH] Sign out error:', error);
        throw error;
      }
      
      setUser(null);
      setUserProfile(null);
      setSession(null);
      toast.success('Signed out successfully');
    } catch (error) {
      console.error('[AUTH] Sign out error:', error);
      toast.error('Error signing out');
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          username: updates.username,
          bio: updates.bio,
          tagline: updates.tagline,
          long_bio: updates.longBio,
          focus_tags: updates.focusTags,
          external_links: updates.externalLinks,
          avatar_id: updates.avatarId,
          is_subscriber: updates.isSubscriber,
          is_donator: updates.isDonator,
          hide_comments: updates.hideComments,
          hide_likes: updates.hideLikes,
          hide_posts: updates.hidePosts,
        })
        .eq('id', user.id);

      if (error) throw error;

      setUserProfile(prev => prev ? { ...prev, ...updates } : null);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('[AUTH] Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchUserProfile(user.id);
    }
  };

  const value: AuthContextType = {
    user,
    userProfile,
    session,
    loading,
    signOut,
    signIn,
    signUp,
    signInWithSocialProvider,
    updateProfile,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
